<template>
  <base-modal
    name="basic-modal"
    :visible="show"
    :close-button="false"
    close-outside
    @close="closeModal"
  >
    <div class="flex flex-col">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-x-3">
          <!-- <div
            class="text-color-1 p-3 bg-bg-4 rounded-sm cursor-pointer"
            @click="updateStatus"
          >
            <svg-icon name="ic-power" />
          </div> -->

          <div
            class="font-semibold"
            :class="{
              'text-success': formData.status === PAYMENT_LINK_STATUS.active,
              'text-paused': formData.status === PAYMENT_LINK_STATUS.paused,
              'text-error': formData.status === PAYMENT_LINK_STATUS.expired,
            }"
          >
            {{ $t(`PaymentLinks | status.${formData.status}`) }}
          </div>

          <div
            class="flex items-center gap-x-2 border border-bg-5 px-2 rounded-sm cursor-pointer"
            @click="onCopyUUID"
          >
            <div class="font-medium text-color-3">
              {{ formatUUID }}
            </div>

            <svg-icon class="text-brand" name="ic-copy" />
          </div>
        </div>

        <div class="cursor-pointer hover:primary-hover" @click="show = false">
          <svg-icon class="!w-6 !h-6 text-color-1" name="ic-close" />
        </div>
      </div>

      <h3
        class="text-2xl font-bold leading-6 text-color-1 mt-5 max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {{ formData.data.title }}
      </h3>

      <div
        v-if="!formData.data.description"
        class="text-sm text-color-5 mt-0.5"
      >
        {{ $t('PaymentLinks | noAdditionalDescription') }}
      </div>

      <div v-else class="text-sm text-color-2 mt-0.5">
        {{ formData.data.description }}
      </div>

      <base-button class="mt-6" size="sm" type="shade" @onClick="onUpdate">
        {{ $t('edit') }}
      </base-button>

      <div class="flex justify-between mt-8">
        <div class="flex flex-col gap-y-0.5">
          <div class="font-medium text-color-3">
            {{
              $t('createdAt', {
                date: formatPaymentLinkDate(new Date(), locale),
              })
            }}
          </div>

          <div class="font-medium text-color-1">
            {{
              formData.expiration.ui_type === PAYMENT_LINK_TYPES.ONE_TIME
                ? $t('PaymentLinks | oneTime')
                : `${$t('PaymentLinks | reusable')} ${$t(getLabelPaymentPeriod())} `
            }}
          </div>
        </div>

        <div class="flex flex-col gap-y-0.5 text-right">
          <div class="font-medium text-color-3">
            {{ $t('until', { date: parseLabelReusableEndDate() }) }}
          </div>

          <div class="font-medium text-color-1">
            {{ remainingTime }}
          </div>
        </div>
      </div>

      <custom-divider margin="my-4" />

      <div class="flex justify-between">
        <div class="flex flex-col gap-y-0.5">
          <div class="font-medium text-color-3">
            {{ $t('PaymentLinks | emcdWalletLabel') }}
          </div>

          <div class="flex gap-x-1 items-center">
            <img
              class="w-6 h-6"
              :src="coin ? coin.media_url : ''"
              :alt="coin ? coin.title : ''"
            />
            <div class="font-medium text-color-1">
              {{ coin ? coin.title : '' }} • {{ network ? network.title : '' }}
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-y-0.5 text-right">
          <div class="font-medium text-color-3">
            {{
              formData.data.amount
                ? $t('PaymentLinks | fixedAmount')
                : $t('PaymentLinks | flexibleAmount')
            }}
          </div>

          <div class="font-medium text-color-1">
            {{
              formData.data.amount
                ? Number(formData.data.amount).toFixed(8)
                : `${Number(formData.amount_boundaries.min_amount).toFixed(8)} - ${Number(formData.amount_boundaries.max_amount).toFixed(8)}`
            }}
            {{
              formData.data.coin_id ? formData.data.coin_id.toUpperCase() : ''
            }}
          </div>
        </div>
      </div>

      <success-view
        v-if="showQrCode"
        :id="id"
        :qr-code-url="qrCodeUrl"
        @copy-qr-code="onCopyQrCode"
        @copy-link="onCopy"
      />

      <details-view
        v-else
        :total-amount="formData.stats?.total_payment_amount || 0"
        @copy-link="onCopy"
      />
    </div>
  </base-modal>
</template>

<script setup>
import { computed, reactive, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

import QRCode from 'qrcode'

import BaseModal from '@emcd-shared/base-modal'
import SuccessView from './views/SuccessView.vue'
import DetailsView from './views/DetailsView.vue'
import BaseButton from '@/shared/ui/BaseButton/BaseButton.vue'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'
import CustomDivider from '@/shared/ui/CustomDivider/CustomDivider.vue'

import { formatPaymentLinkDate } from '@/shared/lib/utils/date'

import {
  PAYMENT_LINK_DISPATCHERS,
  PAYMENT_LINK_GETTERS,
} from '@/shared/constants/store-constants/paymentLink'
import {
  PAYMENT_LINK_PERIOD_PAYMENTS,
  PAYMENT_LINK_STATUS,
  PAYMENT_LINK_TYPES,
} from '@/shared/constants/paymentLink'
import { TIME_UNITS } from '@/shared/constants/formatter'

import { getPlural } from '@/shared/lib/utils/formatters'
import {
  APP_DISPATCHERS,
  APP_GETTERS,
} from '@/shared/constants/store-constants/app'
import CONFIG from '@/app/config/config'
import { ROUTES } from '@/shared/constants/routes'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    default: () => ({}),
  },
  showQrCode: {
    type: Boolean,
    default: true,
  },
})

const emits = defineEmits(['update:modelValue', 'onUpdate'])

const { t, locale } = useI18n()
const store = useStore()
const router = useRouter()

let intervalEndDate = null

const formData = reactive({
  data: {
    title: '',
    description: '',
    coin_id: null,
    network_id: null,
    amount: null,
  },
  expiration: {
    deadline: new Date(),
    ui_type: PAYMENT_LINK_TYPES.ONE_TIME,
  },
  amount_boundaries: {
    max_amount: null,
    min_amount: null,
  },
  status: PAYMENT_LINK_STATUS.active,
  is_one_time: false,
  payment_type: null,
  stats: {
    successful_invoices: 0,
    total_payment_amount: 0,
  },
})

const remainingTime = ref('')

const qrCodeUrl = ref('')

const show = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emits('update:modelValue', value)
  },
})

const formDataStore = computed(
  () => store.getters[PAYMENT_LINK_GETTERS.formData]
)

const id = computed(() => store.getters[PAYMENT_LINK_GETTERS.id])

const formatUUID = computed(() => {
  if (!id.value) return ''
  const firstPart = id.value.slice(0, 6)
  const lastPart = id.value.slice(-6)
  return `ID ${firstPart} ••• ${lastPart}`
})

const storeCoins = computed(() => store.getters[APP_GETTERS.coins])

const coin = computed(() =>
  storeCoins.value.find((item) => item.id === formData.data.coin_id)
)

const network = computed(() =>
  coin.value
    ? coin.value.networks.find((item) => item.id === formData.data.network_id)
    : null
)

function parseData() {
  if (!props.item || typeof props.item !== 'object') {
    return
  }

  Object.keys(props.item).forEach((key) => {
    if (props.item[key] !== undefined) {
      formData[key] = props.item[key]
    }
  })
}

function getLabelPaymentPeriod() {
  switch (formData.expiration.ui_type) {
    case PAYMENT_LINK_PERIOD_PAYMENTS.ONE_DAY:
      return 'PaymentLinks | periodDescriptions.for_one_day'
    case PAYMENT_LINK_PERIOD_PAYMENTS.WEEK:
      return 'PaymentLinks | periodDescriptions.for_seven_day'
    case PAYMENT_LINK_PERIOD_PAYMENTS.TWO_WEEK:
      return 'PaymentLinks | periodDescriptions.for_two_weeks'
    case PAYMENT_LINK_PERIOD_PAYMENTS.MONTH:
      return 'PaymentLinks | periodDescriptions.for_month'
  }
}

function parseLabelReusableEndDate() {
  const date = new Date(formData.expiration.deadline)
  return formatPaymentLinkDate(date, locale)
}

function getRemainingTime() {
  const now = new Date()
  const end = new Date(formData.expiration.deadline)
  const timeDifference = end.getTime() - now.getTime()

  if (timeDifference <= 0) {
    remainingTime.value = t('PaymentLinks | timeExpired')
    return
  }

  const days = Math.floor(timeDifference / (24 * 60 * 60 * 1000))
  const hours = Math.floor(
    (timeDifference % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
  )
  const minutes = Math.floor((timeDifference % (60 * 60 * 1000)) / (60 * 1000))

  remainingTime.value =
    `${t('PaymentLinks | timeLeft')} ${days} ${t(getPlural(days, TIME_UNITS.days))}, ` +
    `${hours} ${t(getPlural(hours, TIME_UNITS.hours))}, ` +
    `${minutes} ${t(getPlural(minutes, TIME_UNITS.minutes))}`
}

function onUpdate() {
  emits('onUpdate')
}

function closeModal() {
  show.value = false
  router.push(ROUTES.PAYMENT_LINKS.LIST)
}

function updateStatus() {
  if (formData.status === PAYMENT_LINK_STATUS.active) {
    store.dispatch(PAYMENT_LINK_DISPATCHERS.updatePaymentLinkStatus, {
      status: PAYMENT_LINK_STATUS.paused,
    })

    return
  }

  store.dispatch(PAYMENT_LINK_DISPATCHERS.updatePaymentLinkStatus, {
    status: PAYMENT_LINK_STATUS.active,
  })
}

async function generateQRCode() {
  qrCodeUrl.value = await QRCode.toDataURL(
    `${CONFIG.PAY_FORM}?formId=${id.value}`
  )
}

async function onCopyQrCode() {
  const response = await fetch(qrCodeUrl.value)
  const blob = await response.blob()

  await store.dispatch(APP_DISPATCHERS.copyFileToClipboard, blob)
}

function onCopy() {
  const paymentLink = `${CONFIG.PAY_FORM}?formId=${id.value}`
  store.dispatch(APP_DISPATCHERS.copyToClipboard, paymentLink)
}

function onCopyUUID() {
  store.dispatch(APP_DISPATCHERS.copyToClipboard, id.value)
}

function copyId() {
  store.dispatch(APP_DISPATCHERS.copyToClipboard, id.value)
}

watch(
  () => formDataStore.value,
  (newValue) => {
    if (newValue) {
      parseData()
    }
  },
  { deep: true }
)

watch(id, (value) => {
  if (!value) {
    return
  }

  generateQRCode()
})

watch(show, (value) => {
  if (value) {
    parseData()
    getRemainingTime()
    intervalEndDate = setInterval(getRemainingTime, 1000)
  } else if (intervalEndDate) {
    clearInterval(intervalEndDate)
  }
})

watch(
  () => props.item,
  (newItem) => {
    if (newItem && typeof newItem === 'object') {
      parseData()
    }
  },
  { deep: true }
)
</script>
